import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  apiService,
  BackendType,
  CDNProvider,
  InfraBackendInput,
  InfraCDNSlot,
  InfraRelayInput,
  InfraWireGuardInput,
  PeopleDropdownItem,
  Project,
  ProjectTenant,
  RedTeamDomain,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useMsal } from "@azure/msal-react";
import {
  getPreferredMsalAccount,
} from "../../utils/msalAccountUtils";
import { getUserDisplayName, getUserEmail } from "../../utils/authUtils";
import {
  AZURE_RELAY_REGION_OPTIONS,
  BACKEND_TYPE_OPTIONS,
  AzureRelayRegion,
  azureRelayLocationOptions,
  buildTerraformPreview,
  defaultRelayProfileForType,
  formatTerraformForCopy,
  normalizeCodename,
  relayProfileOptionsForBackendType,
} from "./terraformPreview";
import { InfraEditSession } from "./types";
import { Message } from "primereact/message";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

/** Format employee for terraform project_team (lowercase first name). */
function formatTeamMemberHandle(person: PeopleDropdownItem): string {
  return person.name.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "");
}

function normalizeFqdn(value: string): string {
  return value.trim().toLowerCase().replace(/\.$/, "");
}

/** Longest inventory apex that equals or is a DNS suffix of the FQDN
 *  (e.g. test.northwind.test → northwind.test). */
function matchInventoryBase(
  fqdn: string,
  inventory: string[],
): string | null {
  const normalized = normalizeFqdn(fqdn);
  if (!normalized) return null;
  let best: string | null = null;
  for (const candidate of inventory) {
    const name = normalizeFqdn(candidate);
    if (!name) continue;
    if (normalized === name || normalized.endsWith(`.${name}`)) {
      if (!best || name.length > best.length) best = candidate;
    }
  }
  return best;
}

function subdomainPrefix(fqdn: string, base: string): string {
  const normalized = normalizeFqdn(fqdn);
  const baseNorm = normalizeFqdn(base);
  if (!normalized || !baseNorm || normalized === baseNorm) return "";
  if (normalized.endsWith(`.${baseNorm}`)) {
    return normalized.slice(0, normalized.length - baseNorm.length - 1);
  }
  return "";
}

function relayTenantLabel(tenant: ProjectTenant): string {
  return tenant.tenant_provider || tenant.tenant_tf_name || tenant.tenant_id;
}

function newBackend(type: BackendType = "mythic"): InfraBackendInput {
  const profile = defaultRelayProfileForType(type);
  return profile
    ? { type, relay_profile_type: profile }
    : { type };
}

const CDN_PROVIDER_OPTIONS: Array<{ label: string; value: CDNProvider }> = [
  { label: "Azure Web Apps", value: "azure" },
  { label: "Azure Front Door", value: "frontdoor" },
  { label: "Fastly", value: "fastly" },
];

const CDN_DOMAIN_FIXED_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

function normalizeRelayCDNSlots(slots: InfraCDNSlot[] | undefined): InfraCDNSlot[] {
  return (slots || [])
    .map((slot) => {
      let provider = (slot.provider || "").trim().toLowerCase();
      if (provider === "azure_frontdoor") provider = "frontdoor";
      if (
        provider !== "azure" &&
        provider !== "frontdoor" &&
        provider !== "fastly"
      ) {
        return null;
      }
      const fixed = (slot.domain_fixed || "").trim().toLowerCase();
      return fixed
        ? { provider: provider as CDNProvider, domain_fixed: fixed }
        : { provider: provider as CDNProvider };
    })
    .filter((slot): slot is InfraCDNSlot => !!slot);
}

type Props = {
  editSession?: InfraEditSession | null;
  onEditConsumed?: () => void;
  onSubmitted?: () => void;
};

const RequestInfrastructure: React.FC<Props> = ({
  editSession = null,
  onEditConsumed,
  onSubmitted,
}) => {
  const { accounts, instance } = useMsal();
  const account = getPreferredMsalAccount(instance, accounts);
  const toast = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [domains, setDomains] = useState<RedTeamDomain[]>([]);
  const [people, setPeople] = useState<PeopleDropdownItem[]>([]);
  const [projectReference, setProjectReference] = useState<string | null>(null);
  const [projectCodename, setProjectCodename] = useState("");
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [tenantLabel, setTenantLabel] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [channelName, setChannelName] = useState("");
  const [clientKey, setClientKey] = useState("1");
  const [basedOnId, setBasedOnId] = useState<number | null>(null);
  const [checkingTenants, setCheckingTenants] = useState(false);
  const [extraTenantAllowed, setExtraTenantAllowed] = useState(false);
  const [projectTeam, setProjectTeam] = useState<string[]>([]);
  const [azureRelayRegion, setAzureRelayRegion] =
    useState<AzureRelayRegion>("europe");
  const [backends, setBackends] = useState<InfraBackendInput[]>([]);
  const [relays, setRelays] = useState<InfraRelayInput[]>([]);
  const [wireguards, setWireguards] = useState<InfraWireGuardInput[]>([{}]);
  const [submitting, setSubmitting] = useState(false);

  const requesterName =
    getUserDisplayName(account) || getUserEmail(account) || "";
  const owner = requesterName;
  const createdBy = requesterName;
  const projectSelectSeq = useRef(0);

  useEffect(() => {
    (async () => {
      const [projectRes, domainRes, peopleRes] = await Promise.all([
        apiService.getProjects(),
        apiService.getRedTeamDomains({ status: "all" }),
        apiService.getPeopleDropdown(),
      ]);
      if (projectRes.success) setProjects(projectRes.data || []);
      if (domainRes.success) setDomains(domainRes.data || []);
      if (peopleRes.success) setPeople(peopleRes.data || []);
      else toast.showError("People", peopleRes.message || "Unable to load employees");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editSession) return;
    const { draft, basedOnId: sourceId } = editSession;
    setBasedOnId(sourceId);
    setProjectReference(draft.project_reference || null);
    setProjectCodename(draft.project_codename || "");
    setTenantId(draft.tenant_id || null);
    setTenantLabel(
      editSession.tenantLabel ||
        (draft.tenant_id ? String(draft.tenant_id) : ""),
    );
    setWebhookUrl(draft.webhook_url || "");
    setChannelName(draft.channel_name || "");
    setClientKey(draft.client_key || "1");
    setProjectTeam(draft.project_team || []);
    setAzureRelayRegion(draft.azure_relay_region || "europe");
    setBackends(draft.backends || []);
    setRelays(draft.relays || []);
    setWireguards(
      draft.wireguards && draft.wireguards.length > 0
        ? draft.wireguards
        : [{}],
    );
    onEditConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSession]);

  const selectedProject = projects.find((p) => p.reference === projectReference);

  const projectOptions = projects.map((p) => ({
    label: `${p.reference || ""} — ${p.name}`,
    value: p.reference || "",
  }));

  const domainOptions = domains.map((d) => ({
    label: d.domainname,
    value: d.domainname,
  }));
  const inventoryDomainNames = useMemo(
    () => domains.map((d) => d.domainname),
    [domains],
  );

  const teamOptions = useMemo(() => {
    const options = people.map((person) => {
      const handle = formatTeamMemberHandle(person);
      return {
        label: `${person.name} ${person.surname}`.trim(),
        value: handle,
        person,
      };
    });
    const known = new Set(options.map((o) => o.value));
    projectTeam.forEach((handle) => {
      if (handle && !known.has(handle)) {
        options.push({
          label: handle,
          value: handle,
          person: { id: -1, name: handle, surname: "" },
        });
      }
    });
    return options.filter((o) => o.value);
  }, [people, projectTeam]);

  const formattedProjectTeam = useMemo(
    () =>
      projectTeam
        .map((m) => m.trim().toLowerCase())
        .filter(Boolean),
    [projectTeam],
  );

  const relayLocationOptionGroups = useMemo(() => {
    const flat = azureRelayLocationOptions(azureRelayRegion);
    const groups: Array<{
      label: string;
      items: Array<{ label: string; value: string }>;
    }> = [];
    for (const opt of flat) {
      let group = groups.find((g) => g.label === opt.group);
      if (!group) {
        group = { label: opt.group, items: [] };
        groups.push(group);
      }
      group.items.push({ label: opt.label, value: opt.value });
    }
    return groups;
  }, [azureRelayRegion]);

  const clearSelectedProject = () => {
    setProjectReference(null);
    setProjectCodename("");
    setTenantId(null);
    setTenantLabel("");
    setCheckingTenants(false);
    setExtraTenantAllowed(false);
  };

  const onSelectProject = async (reference: string | null) => {
    const seq = ++projectSelectSeq.current;
    setProjectReference(reference);
    setTenantId(null);
    setTenantLabel("");
    setExtraTenantAllowed(false);
    const project = projects.find((p) => p.reference === reference);
    if (project) {
      setProjectCodename(normalizeCodename(project.name));
    }

    // New requests assign a tenant on submit. Warn first when one is already reserved.
    if (basedOnId || !reference) {
      setCheckingTenants(false);
      return;
    }

    setCheckingTenants(true);
    const tenants = await apiService.getProjectTenants(reference);
    if (seq !== projectSelectSeq.current) return;
    setCheckingTenants(false);
    if (!tenants.success) {
      toast.showError(
        "Tenants",
        tenants.message || "Unable to check existing tenants",
      );
      clearSelectedProject();
      return;
    }

    const assigned = (tenants.data || []).filter((tenant) => tenant.relay_tenant);
    if (assigned.length === 0) {
      setExtraTenantAllowed(true);
      return;
    }

    const labels = assigned.map(relayTenantLabel).filter(Boolean);
    const listed = labels.join(", ");
    const tenantWord = assigned.length === 1 ? "tenant" : "tenants";
    confirmDialog({
      header: "Project already has a tenant",
      message: `This project already has ${tenantWord} ${listed} assigned. Continuing creates new infrastructure and assigns another tenant when you submit.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Continue with this project",
      rejectLabel: "Cancel",
      accept: () => {
        if (seq !== projectSelectSeq.current) return;
        setExtraTenantAllowed(true);
      },
      reject: () => {
        if (seq !== projectSelectSeq.current) return;
        clearSelectedProject();
      },
    });
  };

  const updateBackend = (index: number, next: InfraBackendInput) => {
    setBackends((prev) => {
      const backendsNext = [...prev];
      backendsNext[index] = next;
      return backendsNext;
    });
    // Keep attached relays in sync with backend type + profile.
    setRelays((prev) =>
      prev.map((relay) => {
        if (relay.standalone || relay.connected_to_backend !== index + 1) {
          return relay;
        }
        return {
          ...relay,
          type: next.type,
          relay_profile_type: next.relay_profile_type,
        };
      }),
    );
  };

  const relaysForSubmit = (): InfraRelayInput[] =>
    relays.map((relay) => {
      const cdn_slots = normalizeRelayCDNSlots(relay.cdn_slots);
      if (relay.standalone || relay.type === "relay") {
        return {
          ...relay,
          type: "relay",
          connected_to_backend: 0,
          relay_profile_type: undefined,
          cdn_slots,
          cdn_domains_count: undefined,
        };
      }
      const backend = backends[relay.connected_to_backend - 1];
      const profile =
        backend?.relay_profile_type ||
        defaultRelayProfileForType(backend?.type || relay.type);
      return {
        ...relay,
        type: backend?.type || relay.type,
        relay_profile_type: profile,
        cdn_slots,
        cdn_domains_count: undefined,
      };
    });

  const updateRelayCDNSlot = (
    relayIndex: number,
    slotIndex: number,
    next: InfraCDNSlot,
  ) => {
    setRelays((prev) => {
      const relaysNext = [...prev];
      const relay = relaysNext[relayIndex];
      const slots = [...(relay.cdn_slots || [])];
      slots[slotIndex] = next;
      relaysNext[relayIndex] = { ...relay, cdn_slots: slots };
      return relaysNext;
    });
  };

  const addRelayCDNSlot = (relayIndex: number) => {
    setRelays((prev) => {
      const relaysNext = [...prev];
      const relay = relaysNext[relayIndex];
      relaysNext[relayIndex] = {
        ...relay,
        cdn_slots: [...(relay.cdn_slots || []), { provider: "azure" }],
      };
      return relaysNext;
    });
  };

  const removeRelayCDNSlot = (relayIndex: number, slotIndex: number) => {
    setRelays((prev) => {
      const relaysNext = [...prev];
      const relay = relaysNext[relayIndex];
      relaysNext[relayIndex] = {
        ...relay,
        cdn_slots: (relay.cdn_slots || []).filter((_, i) => i !== slotIndex),
      };
      return relaysNext;
    });
  };

  const renderCDNSlots = (relay: InfraRelayInput, relayIndex: number) => (
    <div className="col-12">
      <div className="flex justify-content-between align-items-center mb-2">
        <div>
          <label className="block text-sm font-medium m-0">CDN slots</label>
          <small className="text-600">
            Blank name = random. Azure Web Apps → *.azurewebsites.net; Azure
            Front Door → Azure Front Door endpoint; Fastly →
            *.global.ssl.fastly.net
          </small>
        </div>
        <Button
          label="Add CDN"
          icon="pi pi-plus"
          className="p-button-sm p-button-outlined"
          onClick={() => addRelayCDNSlot(relayIndex)}
        />
      </div>
      {(relay.cdn_slots || []).length === 0 && (
        <div className="text-600 text-sm mb-2">No CDN slots.</div>
      )}
      {(relay.cdn_slots || []).map((slot, slotIndex) => (
        <div
          key={`cdn-${relayIndex}-${slotIndex}`}
          className="grid align-items-end mb-2"
        >
          <div className="col-12 md:col-3">
            <label className="block mb-1 text-sm font-medium">
              Slot {slotIndex + 1} provider
            </label>
            <Dropdown
              value={slot.provider}
              options={CDN_PROVIDER_OPTIONS}
              onChange={(e) =>
                updateRelayCDNSlot(relayIndex, slotIndex, {
                  ...slot,
                  provider: e.value,
                })
              }
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-7">
            <label className="block mb-1 text-sm font-medium">
              CDN name (optional)
            </label>
            <InputText
              value={slot.domain_fixed || ""}
              onChange={(e) =>
                updateRelayCDNSlot(relayIndex, slotIndex, {
                  ...slot,
                  domain_fixed: e.target.value || undefined,
                })
              }
              className="w-full"
              placeholder="Random if empty"
            />
          </div>
          <div className="col-12 md:col-2 flex align-items-end">
            <Button
              icon="pi pi-trash"
              className="p-button-text p-button-danger p-button-sm w-full"
              onClick={() => removeRelayCDNSlot(relayIndex, slotIndex)}
              aria-label={`Remove CDN slot ${slotIndex + 1}`}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const preview = useMemo(
    () =>
      buildTerraformPreview({
        clientKey,
        projectCode: projectReference || "",
        projectCodename,
        owner,
        createdBy,
        azureRelayRegion,
        projectTeam: formattedProjectTeam,
        tenantProvider: tenantLabel,
        webhookUrl,
        channelName,
        backends,
        relays,
        wireguards,
      }),
    [
      clientKey,
      projectReference,
      projectCodename,
      owner,
      createdBy,
      azureRelayRegion,
      formattedProjectTeam,
      tenantLabel,
      webhookUrl,
      channelName,
      backends,
      relays,
      wireguards,
    ],
  );

  const doSubmit = async () => {
    if (!projectReference) return;

    const preferredTenantId = basedOnId ? tenantId : null;
    setSubmitting(true);
    const res = await apiService.createInfrastructureRequest({
      project_reference: projectReference,
      project_codename: projectCodename,
      owner,
      created_by: createdBy,
      azure_relay_region: azureRelayRegion,
      project_team: formattedProjectTeam,
      ...(preferredTenantId ? { tenant_id: preferredTenantId } : {}),
      webhook_url: webhookUrl.trim(),
      channel_name: channelName.trim(),
      client_key: clientKey,
      backends,
      relays: relaysForSubmit(),
      wireguards,
      based_on_id: basedOnId || undefined,
    });
    setSubmitting(false);
    if (res.success) {
      const assignedLabel =
        res.data.tenant_provider ||
        (res.data.tenant_id ? String(res.data.tenant_id) : "");
      if (
        preferredTenantId &&
        (res.data.tenant_swapped || res.data.tenant_id !== preferredTenantId)
      ) {
        setTenantId(res.data.tenant_id);
        setTenantLabel(assignedLabel);
        toast.showWarn(
          "Tenant changed",
          `Preferred tenant was taken; reserved ${assignedLabel} instead`,
        );
      }
      const onTenant = assignedLabel ? ` on tenant ${assignedLabel}` : "";
      toast.showSuccess(
        basedOnId ? "Updated submission" : "Requested",
        basedOnId
          ? `New infrastructure #${res.data.id} created from #${basedOnId}${onTenant}`
          : `Infrastructure #${res.data.id} created${onTenant}`,
      );
      setBasedOnId(null);
      onSubmitted?.();
    } else {
      toast.showError("Error", res.message || "Request failed");
    }
  };

  const submit = async () => {
    if (!projectReference) {
      toast.showWarn("Validation", "Select a project");
      return;
    }
    if (!basedOnId && !extraTenantAllowed) {
      toast.showWarn(
        "Validation",
        checkingTenants
          ? "Still checking whether this project already has a tenant"
          : "Confirm the additional tenant before submitting",
      );
      return;
    }
    if (wireguards.length < 1) {
      toast.showWarn("Validation", "At least one WireGuard relay is required");
      return;
    }
    for (const relay of relays) {
      if (!relay.standalone && !relay.domain?.trim()) {
        toast.showWarn("Validation", "Attached relays require a domain FQDN");
        return;
      }
    }

    const seenFqdns = new Map<string, number>();
    for (let i = 0; i < relays.length; i++) {
      const fqdn = (relays[i].domain || "").trim().toLowerCase().replace(/\.$/, "");
      if (!fqdn) continue;
      if (seenFqdns.has(fqdn)) {
        toast.showWarn(
          "Validation",
          `Domain "${fqdn}" is already used on another relay. The same FQDN (including subdomain) cannot be reused.`,
        );
        return;
      }
      seenFqdns.set(fqdn, i);
    }

    for (let i = 0; i < relays.length; i++) {
      const slots = relays[i].cdn_slots || [];
      for (let s = 0; s < slots.length; s++) {
        let provider = (slots[s].provider || "").trim().toLowerCase();
        if (provider === "azure_frontdoor") provider = "frontdoor";
        if (
          provider !== "azure" &&
          provider !== "frontdoor" &&
          provider !== "fastly"
        ) {
          toast.showWarn(
            "Validation",
            `Relay #${i + 1} CDN slot ${s + 1}: choose Azure Web Apps, Azure Front Door, or Fastly`,
          );
          return;
        }
        const fixed = (slots[s].domain_fixed || "").trim().toLowerCase();
        if (!fixed) continue;
        if (fixed.length > 60 || !CDN_DOMAIN_FIXED_PATTERN.test(fixed)) {
          toast.showWarn(
            "Validation",
            `Relay #${i + 1} CDN slot ${s + 1}: name must be lowercase letters, digits, and hyphens`,
          );
          return;
        }
      }
    }

    await doSubmit();
  };

  const sectionTitle = (title: string, actions?: React.ReactNode) => (
    <div className="flex justify-content-between align-items-center mb-2 mt-2">
      <h3 className="m-0 text-lg text-900">{title}</h3>
      {actions}
    </div>
  );

  const blockClass = "border-round-xl p-3 mb-3";

  const blobStyle = (
    accent: string,
    tint: string,
    extra?: React.CSSProperties,
  ): React.CSSProperties => ({
    background: tint,
    boxShadow:
      "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 14px rgba(15, 23, 42, 0.08)",
    borderLeft: `3px solid ${accent}`,
    borderTop: "1px solid rgba(15, 23, 42, 0.04)",
    borderRight: "1px solid rgba(15, 23, 42, 0.04)",
    borderBottom: "1px solid rgba(15, 23, 42, 0.04)",
    ...extra,
  });

  const projectBlockStyle = blobStyle(
    "var(--blue-500, #3b82f6)",
    "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02))",
  );
  const backendBlockStyle = blobStyle(
    "var(--teal-500, #14b8a6)",
    "linear-gradient(135deg, rgba(20, 184, 166, 0.10), rgba(20, 184, 166, 0.03))",
  );
  const relayBlockStyle = blobStyle(
    "var(--orange-500, #f97316)",
    "linear-gradient(135deg, rgba(249, 115, 22, 0.10), rgba(249, 115, 22, 0.03))",
  );
  const wireguardBlockStyle = blobStyle(
    "var(--green-500, #22c55e)",
    "linear-gradient(135deg, rgba(34, 197, 94, 0.10), rgba(34, 197, 94, 0.03))",
  );
  const previewStyle = blobStyle(
    "var(--indigo-500, #6366f1)",
    "linear-gradient(160deg, rgba(99, 102, 241, 0.10), rgba(99, 102, 241, 0.03))",
    {
      position: "sticky",
      top: "1rem",
      maxHeight: "calc(100vh - 6rem)",
      overflow: "auto",
      marginBottom: 0,
    },
  );

  return (
    <div className="flex flex-column gap-2">
      <ConfirmDialog />
      <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
        <h2 className="m-0 text-xl">
          {basedOnId
            ? `Modify infrastructure (from #${basedOnId})`
            : "Request infrastructure"}
        </h2>
        <div className="flex gap-2">
          {basedOnId && (
            <Button
              label="Cancel modify"
              className="p-button-text p-button-sm"
              onClick={() => {
                setBasedOnId(null);
                onSubmitted?.();
              }}
            />
          )}
          <Button
            label={basedOnId ? "Submit new version" : "Submit request"}
            icon="pi pi-check"
            className="p-button-sm"
            loading={submitting || checkingTenants}
            disabled={!selectedProject || (!basedOnId && !extraTenantAllowed)}
            onClick={submit}
          />
        </div>
      </div>
      {basedOnId && (
        <Message
          severity="info"
          text={`Editing a copy of infrastructure #${basedOnId}. Submitting creates a new request with refreshed terraform JSON; the previous submission is kept.`}
        />
      )}

      <div className="grid" style={{ alignItems: "flex-start" }}>
        <div className="col-12 xl:col-7">
          <div className={blockClass} style={projectBlockStyle}>
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block mb-1 text-sm font-medium">Project</label>
                <Dropdown
                  value={projectReference}
                  options={projectOptions}
                  onChange={(e) => onSelectProject(e.value)}
                  placeholder="Select project"
                  filter
                  className="w-full"
                  disabled={!!basedOnId}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-1 text-sm font-medium">
                  Project codename
                </label>
                <InputText
                  value={projectCodename}
                  onChange={(e) => setProjectCodename(e.target.value)}
                  className="w-full"
                  placeholder="lowercase-numbers-and-dashes"
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="block mb-1 text-sm font-medium">
                  Azure relay region
                </label>
                <Dropdown
                  value={azureRelayRegion}
                  options={AZURE_RELAY_REGION_OPTIONS}
                  onChange={(e) => setAzureRelayRegion(e.value)}
                  className="w-full"
                />
              </div>
              <div className="col-12">
                <label className="block mb-1 text-sm font-medium">
                  Project team
                </label>
                <MultiSelect
                  value={projectTeam}
                  options={teamOptions}
                  onChange={(e) => setProjectTeam(e.value || [])}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select employees"
                  filter
                  display="chip"
                  className="w-full"
                  showClear
                />
                {formattedProjectTeam.length > 0 && (
                  <small className="text-600 block mt-1">
                    project_team: [
                    {formattedProjectTeam.map((m) => `"${m}"`).join(", ")}]
                  </small>
                )}
              </div>
              {basedOnId ? (
                <div className="col-12 md:col-8">
                  <label className="block mb-1 text-sm font-medium">
                    Tenant
                  </label>
                  <InputText
                    value={tenantLabel}
                    readOnly
                    className="w-full"
                  />
                </div>
              ) : (
                <div className="col-12">
                  <Message
                    severity="info"
                    text="A tenant is assigned automatically when you submit this request."
                  />
                </div>
              )}
              <div className="col-12 md:col-8">
                <label className="block mb-1 text-sm font-medium">
                  Slack webhook URL
                </label>
                <InputText
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="w-full"
                  placeholder="https://hooks.slack.com/..."
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="block mb-1 text-sm font-medium">
                  Slack channel name
                </label>
                <InputText
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  className="w-full"
                  placeholder="twilight-mythic"
                />
              </div>
            </div>
          </div>

          {sectionTitle(
            "Backends",
            <Button
              label="Add backend"
              icon="pi pi-plus"
              className="p-button-sm"
              onClick={() => setBackends((prev) => [...prev, newBackend()])}
            />,
          )}
          {backends.length === 0 && (
            <div className="text-600 text-sm mb-2">
              No backends yet (Morpheus / Mythic / …).
            </div>
          )}
          {backends.map((backend, index) => {
            const profileOptions = relayProfileOptionsForBackendType(
              backend.type,
            );
            return (
              <div key={`backend-${index}`} className={blockClass} style={backendBlockStyle}>
                <div className="flex justify-content-between align-items-center mb-2">
                  <strong>Backend #{index + 1}</strong>
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-danger p-button-sm"
                    onClick={() => {
                      setBackends(backends.filter((_, i) => i !== index));
                      setRelays((prev) =>
                        prev
                          .filter(
                            (r) =>
                              r.standalone ||
                              r.connected_to_backend !== index + 1,
                          )
                          .map((r) =>
                            !r.standalone && r.connected_to_backend > index + 1
                              ? {
                                  ...r,
                                  connected_to_backend:
                                    r.connected_to_backend - 1,
                                }
                              : r,
                          ),
                      );
                    }}
                  />
                </div>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Type
                    </label>
                    <Dropdown
                      value={backend.type}
                      options={BACKEND_TYPE_OPTIONS}
                      onChange={(e) => {
                        const type = e.value as BackendType;
                        updateBackend(index, newBackend(type));
                      }}
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Relay profile
                    </label>
                    {profileOptions ? (
                      <Dropdown
                        value={backend.relay_profile_type || null}
                        options={profileOptions}
                        onChange={(e) =>
                          updateBackend(index, {
                            ...backend,
                            relay_profile_type: e.value || undefined,
                          })
                        }
                        placeholder="Select profile"
                        className="w-full"
                      />
                    ) : (
                      <div className="text-600 text-sm py-2">
                        No profile for this backend type
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {sectionTitle(
            "Attached / standalone relays",
            <div className="flex gap-2 flex-wrap">
              <Button
                label="Add attached relay"
                icon="pi pi-plus"
                className="p-button-sm"
                disabled={backends.length === 0}
                onClick={() => {
                  const backend = backends[0];
                  setRelays((prev) => [
                    ...prev,
                    {
                      type: backend?.type || "mythic",
                      connected_to_backend: 1,
                      relay_profile_type: backend?.relay_profile_type,
                      cdn_slots: [],
                    },
                  ]);
                }}
              />
              <Button
                label="Add standalone relay"
                icon="pi pi-plus"
                className="p-button-sm p-button-outlined"
                onClick={() =>
                  setRelays((prev) => [
                    ...prev,
                    {
                      type: "relay",
                      connected_to_backend: 0,
                      standalone: true,
                      cdn_slots: [],
                    },
                  ])
                }
              />
            </div>,
          )}
          {relays.length === 0 && (
            <div className="text-600 text-sm mb-2">No relays yet.</div>
          )}
          {relays.map((relay, index) => (
            <div key={`relay-${index}`} className={blockClass} style={relayBlockStyle}>
              <div className="flex justify-content-between align-items-center mb-2">
                <strong>
                  {relay.standalone ? "Standalone relay" : "Attached relay"} #
                  {index + 1}
                </strong>
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  onClick={() =>
                    setRelays(relays.filter((_, i) => i !== index))
                  }
                />
              </div>
              {!relay.standalone && (
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Backend
                    </label>
                    <Dropdown
                      value={relay.connected_to_backend}
                      options={backends.map((b, i) => ({
                        label: `#${i + 1} ${b.type}${
                          b.relay_profile_type
                            ? ` (${b.relay_profile_type})`
                            : ""
                        }`,
                        value: i + 1,
                      }))}
                      onChange={(e) => {
                        const next = [...relays];
                        const backend = backends[e.value - 1];
                        next[index] = {
                          ...relay,
                          connected_to_backend: e.value,
                          type: backend?.type || "mythic",
                          relay_profile_type: backend?.relay_profile_type,
                        };
                        setRelays(next);
                      }}
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Domain (base)
                    </label>
                    <Dropdown
                      value={
                        matchInventoryBase(
                          relay.domain || "",
                          inventoryDomainNames,
                        ) || null
                      }
                      options={domainOptions}
                      onChange={(e) => {
                        const base = e.value as string;
                        const current = relay.domain || "";
                        const previousBase = matchInventoryBase(
                          current,
                          inventoryDomainNames,
                        );
                        const sub = previousBase
                          ? subdomainPrefix(current, previousBase)
                          : "";
                        const next = [...relays];
                        next[index] = {
                          ...relay,
                          domain: sub ? `${sub}.${base}` : base,
                        };
                        setRelays(next);
                      }}
                      placeholder="Select domain"
                      filter
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Subdomain + FQDN
                    </label>
                    <InputText
                      value={relay.domain || ""}
                      onChange={(e) => {
                        const next = [...relays];
                        next[index] = { ...relay, domain: e.target.value };
                        setRelays(next);
                      }}
                      className="w-full"
                      placeholder="test.northwind.test"
                    />
                  </div>
                  {renderCDNSlots(relay, index)}
                </div>
              )}
              {relay.standalone && (
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Domain (base, optional)
                    </label>
                    <Dropdown
                      value={
                        matchInventoryBase(
                          relay.domain || "",
                          inventoryDomainNames,
                        ) || null
                      }
                      options={domainOptions}
                      onChange={(e) => {
                        const base = (e.value as string) || "";
                        const current = relay.domain || "";
                        const previousBase = matchInventoryBase(
                          current,
                          inventoryDomainNames,
                        );
                        const sub = previousBase
                          ? subdomainPrefix(current, previousBase)
                          : "";
                        const next = [...relays];
                        next[index] = {
                          ...relay,
                          domain: base
                            ? sub
                              ? `${sub}.${base}`
                              : base
                            : undefined,
                        };
                        setRelays(next);
                      }}
                      placeholder="Select domain"
                      filter
                      showClear
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Subdomain + FQDN (optional)
                    </label>
                    <InputText
                      value={relay.domain || ""}
                      onChange={(e) => {
                        const next = [...relays];
                        next[index] = {
                          ...relay,
                          domain: e.target.value || undefined,
                        };
                        setRelays(next);
                      }}
                      className="w-full"
                      placeholder="test.northwind.test"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-1 text-sm font-medium">
                      Profile
                    </label>
                    <div className="text-600 text-sm py-2">
                      None (standalone relays have no profile)
                    </div>
                  </div>
                  {renderCDNSlots(relay, index)}
                </div>
              )}
              <div className="grid mt-1">
                <div className="col-12">
                  <label className="block mb-1 text-sm font-medium">
                    Azure relay location override (optional)
                  </label>
                  <Dropdown
                    value={relay.azure_relay_location || null}
                    options={relayLocationOptionGroups}
                    onChange={(e) => {
                      const next = [...relays];
                      next[index] = {
                        ...relay,
                        azure_relay_location: e.value || undefined,
                      };
                      setRelays(next);
                    }}
                    optionLabel="label"
                    optionValue="value"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    filter
                    showClear
                    className="w-full"
                    placeholder="Select Azure location"
                  />
                </div>
              </div>
            </div>
          ))}

          {sectionTitle(
            "WireGuard relays",
            <Button
              label="Add WireGuard"
              icon="pi pi-plus"
              className="p-button-sm"
              onClick={() => setWireguards((prev) => [...prev, {}])}
            />,
          )}
          {wireguards.map((wg, index) => (
            <div key={`wg-${index}`} className={blockClass} style={wireguardBlockStyle}>
              <div className="flex justify-content-between align-items-center mb-2">
                <strong>WireGuard #{index + 1}</strong>
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  disabled={wireguards.length <= 1}
                  onClick={() =>
                    setWireguards(wireguards.filter((_, i) => i !== index))
                  }
                />
              </div>
              <label className="block mb-1 text-sm font-medium">
                Azure relay location (optional)
              </label>
              <Dropdown
                value={wg.azure_relay_location || null}
                options={relayLocationOptionGroups}
                onChange={(e) => {
                  const next = [...wireguards];
                  next[index] = {
                    azure_relay_location: e.value || undefined,
                  };
                  setWireguards(next);
                }}
                optionLabel="label"
                optionValue="value"
                optionGroupLabel="label"
                optionGroupChildren="items"
                filter
                showClear
                className="w-full"
                placeholder="Optional azure_relay_location"
              />
            </div>
          ))}
        </div>

        <div className="col-12 xl:col-5">
          <div className="border-round-xl p-3" style={previewStyle}>
            <h3 className="m-0 mb-2 text-lg text-900">Terraform preview</h3>
            <pre className="m-0 text-sm" style={{ whiteSpace: "pre-wrap" }}>
              {formatTerraformForCopy(preview)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestInfrastructure;
